/**
 * One-step methods for the initial-value problem dy/dx = f(x, y), y(x₀) = y₀
 * (Burden & Faires, Numerical Analysis, §5.2 and §5.4), with h = (x_end − x₀)/n:
 *
 *     Euler        yₙ₊₁ = yₙ + h·f(xₙ, yₙ)                              global error O(h)
 *     Heun         yₙ₊₁ = yₙ + (h/2)(k₁ + k₂),  k₁ = f(xₙ, yₙ),
 *                  k₂ = f(xₙ + h, yₙ + h·k₁)                           global error O(h²)
 *     RK4          yₙ₊₁ = yₙ + (h/6)(k₁ + 2k₂ + 2k₃ + k₄)             global error O(h⁴)
 *
 * Heun's method is also called the improved Euler method or the explicit trapezoid rule.
 * Each row of the table keeps the stage slopes, so the arithmetic can be followed.
 */

export type OdeMethod = "euler" | "heun" | "rk4";
export type Slope = (x: number, y: number) => number;

export const MAX_ODE_STEPS = 1000;

export interface OdeRow {
  n: number;
  x: number;
  y: number;
  /** Stage slopes k₁ … used to leave this point (empty for the final row). */
  slopes: number[];
}

export type OdeRun =
  | { kind: "ok"; method: OdeMethod; h: number; rows: OdeRow[] }
  | {
      kind: "blow-up";
      method: OdeMethod;
      h: number;
      rows: OdeRow[];
      at: number;
    };

export const STAGES: Record<OdeMethod, number> = { euler: 1, heun: 2, rk4: 4 };

/** One step from (x, y): the stage slopes and the new y. */
export function stepOnce(
  f: Slope,
  method: OdeMethod,
  x: number,
  y: number,
  h: number
): { slopes: number[]; y: number } {
  const k1 = f(x, y);
  if (method === "euler") {
    return { slopes: [k1], y: y + h * k1 };
  }
  if (method === "heun") {
    const k2 = f(x + h, y + h * k1);
    return { slopes: [k1, k2], y: y + (h / 2) * (k1 + k2) };
  }
  const k2 = f(x + h / 2, y + (h / 2) * k1);
  const k3 = f(x + h / 2, y + (h / 2) * k2);
  const k4 = f(x + h, y + h * k3);
  return {
    slopes: [k1, k2, k3, k4],
    y: y + (h / 6) * (k1 + 2 * k2 + 2 * k3 + k4),
  };
}

/** Integrate from x₀ to x_end in n equal steps. */
export function solveOde(
  f: Slope,
  method: OdeMethod,
  x0: number,
  y0: number,
  xEnd: number,
  n: number
): OdeRun {
  const h = (xEnd - x0) / n;
  const rows: OdeRow[] = [];
  let x = x0;
  let y = y0;
  for (let i = 0; i < n; i += 1) {
    const step = stepOnce(f, method, x, y, h);
    rows.push({ n: i, x, y, slopes: step.slopes });
    if (!(Number.isFinite(step.y) && step.slopes.every(Number.isFinite))) {
      return { kind: "blow-up", method, h, rows, at: x };
    }
    y = step.y;
    x = x0 + (i + 1) * h;
  }
  rows.push({ n, x: xEnd, y, slopes: [] });
  return { kind: "ok", method, h, rows };
}

/**
 * A reference solution at the grid points of an n-step run: RK4 with enough substeps
 * (at least 2000 in all) that its error is far below that of the methods being compared.
 */
export function referenceValues(
  f: Slope,
  x0: number,
  y0: number,
  xEnd: number,
  n: number
): number[] {
  const sub = Math.max(1, Math.ceil(2000 / n));
  const total = n * sub;
  const h = (xEnd - x0) / total;
  const values = [y0];
  let y = y0;
  for (let i = 0; i < total; i += 1) {
    y = stepOnce(f, "rk4", x0 + i * h, y, h).y;
    if ((i + 1) % sub === 0) {
      values.push(y);
    }
  }
  return values;
}
